import { useState } from 'react';

import { useS3, deleteFile } from '@/shared/hooks/useS3';
import Button from '@/shared/components/UI/Button';
import Input from '@/shared/components/UI/Input';
import InputWithButton from '@/shared/components/UI/InputWithButton';
import FileInput from '@/shared/components/UI/FileInput';
import PrivacyPolicyModal from '@/shared/components/UI/PrivacyPolicyModal';
import TermsOfServiceModal from '@/shared/components/UI/TermsOfServiceModal';
import MarketingInfoPolicyModal from '@/shared/components/UI/MarketingInfoPolicyModal';

import { useEmail } from '../hooks/useEmail';
import { useSubmit } from '../hooks/useSubmit';
import { useValidatePassword } from '../hooks/useValidatePassword';
import { SignUpForm } from '../types/SignUpTypes';
import { useValidateSignUp } from '../hooks/useValidateSignUp';

const SignUp = () => {
  const [isPrivacyModalOpen, setIsPrivacyModalOpen] = useState(false);
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
  const [isMarketingModalOpen, setIsMarketingModalOpen] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [signupForm, setSignupForm] = useState<SignUpForm>({
    email: '', // [3, 100] characters
    password: '', // [8, 40] characters, regex: ^(?=.*[A-Za-z])(?=.*\d)(?=.*[^A-Za-z0-9])(?!.*\s).{8,40}$
    companyName: '', // [0, 100] characters
    companyAddress: '', // [0, 255] characters
    representativeName: '', // [0, 100] characters
    phoneNumber: '', // [0, 20] characters, regex: ^[0-9+()\-\s]{1,20}$
    businessRegistrationNumber: '', // [0, 100] characters
    businessCertificateUrl: '', // [0, 2048] characters 파일 url
    postalCode: '', // [0, 50] characters
    isKoreanCompany: null,
    personalInfoAgree: false,
    termsOfServiceAgree: false,
    marketingAgree: false,
  });

  const { handleFileSelect, selectedFile, uploadLoading } = useS3({
    fileType: 'BUSINESS_CERT',
    onUploadSuccess: async (url) => {
      const previousUrl = signupForm.businessCertificateUrl;

      setSignupForm((prev) => ({
        ...prev,
        businessCertificateUrl: url,
      }));

      if (previousUrl && previousUrl.trim() !== '') {
        deleteFile(previousUrl).catch(() => {
          console.error('Failed to delete previous temporary file');
        });
      }
    },
    onUploadError: (error) => {
      console.error('Business certificate upload failed:', error);
    },
  });

  const {
    emailSent,
    isEmailVerified,
    emailVerificationError,
    timer,
    isTimerRunning,
    verificationCode,
    setVerificationCode,
    formatTime,
    checkEmailDuplicate,
    verifyEmailCode,
  } = useEmail({
    signupForm,
    setSignupForm,
  });

  const {
    validatePassword,
    validateConfirmPassword,
    passwordError,
    confirmPasswordError,
  } = useValidatePassword();

  const { validateSignUp, validateField, errors } = useValidateSignUp();

  const { handleSubmit: submitForm } = useSubmit({ signupForm });

  const handleInputChange =
    (field: keyof SignUpForm) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setSignupForm({ ...signupForm, [field]: e.target.value });
    };

  const handleValidatedInputChange =
    (field: keyof typeof errors) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;
      setSignupForm({ ...signupForm, [field]: value });
      validateField(field, value);
    };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPassword = e.target.value;
    setSignupForm({ ...signupForm, password: newPassword });
    validatePassword(newPassword);

    if (confirmPassword) {
      validateConfirmPassword(newPassword, confirmPassword);
    }
  };

  const handleConfirmPasswordChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const confirmedPassword = e.target.value;
    setConfirmPassword(confirmedPassword);
    validateConfirmPassword(signupForm.password, confirmedPassword);
  };

  const handleKoreanCompanySelect = (isKorean: boolean) => () => {
    setSignupForm({ ...signupForm, isKoreanCompany: isKorean });
    validateField('isKoreanCompany', isKorean);
  };

  const handleCheckboxChange =
    (field: 'personalInfoAgree' | 'termsOfServiceAgree' | 'marketingAgree') =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { checked } = e.target;
      setSignupForm({ ...signupForm, [field]: checked });

      if (field !== 'marketingAgree') {
        validateField(field, checked);
      }
    };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const isValid = validateSignUp({
      signupForm,
      isEmailVerified,
      selectedFile,
    });

    if (!isValid) {
      return;
    }

    if (passwordError || confirmPasswordError) {
      return;
    }

    submitForm(e);
  };

  return (
    <section className="mx-auto mb-[80px] mt-[68px] w-[30rem] text-center">
      <h3 className="text-[24px] font-extrabold text-[#95699f]">
        CREATE ACCOUNT
      </h3>
      <form
        method="post"
        onSubmit={handleSubmit}
        className="mx-auto mt-12 w-[30rem]"
        id="create-account-form"
      >
        {/* Email */}
        <InputWithButton
          label="Email"
          type="email"
          name="email"
          placeholder="Enter email"
          value={signupForm.email}
          onChange={handleInputChange('email')}
          buttonText="Send"
          onButtonClick={checkEmailDuplicate}
          buttonDisabled={isEmailVerified || isTimerRunning}
          fullWidth
          disabled={isEmailVerified}
          className="mb-8 w-[30rem]"
        />
        {/* Email Verification */}
        {emailSent && (
          <div className="mb-8">
            <InputWithButton
              label="Email Verification"
              type="text"
              name="email-verification"
              placeholder="Enter verification number"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value)}
              buttonText="Verify"
              onButtonClick={verifyEmailCode}
              buttonDisabled={isEmailVerified || timer === 0}
              rightElement={
                <span className="text-[14px] text-[#d52e2e]">
                  {formatTime(timer)}
                </span>
              }
              fullWidth
              disabled={isEmailVerified}
              className="w-[30rem]"
            />
            {emailVerificationError && (
              <div className="mt-2 flex w-full justify-center text-[14px] text-[#d52e2e]">
                {emailVerificationError}
              </div>
            )}
          </div>
        )}
        {/* Password */}
        <div className="mb-8">
          <Input
            label="Password"
            type="password"
            name="password"
            placeholder="Enter password"
            value={signupForm.password}
            onChange={handlePasswordChange}
            helperText={
              'letters, numbers, special characters (excluding \\ \' " | ~ `) 8-40 characters'
            }
            error={passwordError}
            fullWidth
            className="w-[30rem]"
          />
        </div>
        {/* Password Check */}
        <div className="mb-8">
          <Input
            label="Password Check"
            type="password"
            name="confirm-password"
            placeholder="Enter password"
            value={confirmPassword}
            onChange={handleConfirmPasswordChange}
            error={confirmPasswordError}
            fullWidth
            className="w-[30rem]"
          />
        </div>
        <div className="mb-8 text-left">
          <p className="ml-2 text-[14px] font-semibold leading-[1.4] text-[#444]">
            Company Location
          </p>
          <div className="mt-1 flex gap-2">
            <Button
              type="button"
              variant={
                signupForm.isKoreanCompany === true ? 'primary' : 'outline'
              }
              size="sm"
              onClick={handleKoreanCompanySelect(true)}
              className={`h-9 border-[0.5px] text-[14px] font-normal ${
                signupForm.isKoreanCompany === true
                  ? 'bg-[#b995be] hover:bg-[#a378ad]'
                  : 'border-[#ddd] text-[#444]'
              }`}
              fullWidth
            >
              Korea Company
            </Button>
            <Button
              type="button"
              variant={
                signupForm.isKoreanCompany === false ? 'primary' : 'outline'
              }
              size="sm"
              onClick={handleKoreanCompanySelect(false)}
              className={`h-9 border-[0.5px] text-[14px] font-normal ${
                signupForm.isKoreanCompany === false
                  ? 'bg-[#b995be] hover:bg-[#a378ad]'
                  : 'border-[#ddd] text-[#444]'
              }`}
              fullWidth
            >
              Global Company
            </Button>
          </div>
          {errors.isKoreanCompany && (
            <p className="ml-2 mt-1 text-xs text-red-500">
              {errors.isKoreanCompany}
            </p>
          )}
        </div>
        {/* Business Registration Number */}
        <div className="mb-8">
          <Input
            label="Business Registration Number"
            type="number"
            name="businessNumber"
            placeholder="Enter business registration number"
            value={signupForm.businessRegistrationNumber}
            onChange={handleValidatedInputChange('businessRegistrationNumber')}
            error={errors.businessRegistrationNumber}
            fullWidth
            className="w-[30rem]"
          />
        </div>
        {/* Company Name */}
        <div className="mb-8">
          <Input
            label="Company Name"
            type="text"
            name="company-name"
            placeholder="Enter company name"
            value={signupForm.companyName}
            onChange={handleValidatedInputChange('companyName')}
            error={errors.companyName}
            fullWidth
            className="w-[30rem]"
          />
        </div>
        {/* Representative Name */}
        <div className="mb-8">
          <Input
            label="Representative Name"
            type="text"
            name="representative-name"
            placeholder="Enter representative name"
            value={signupForm.representativeName}
            onChange={handleValidatedInputChange('representativeName')}
            error={errors.representativeName}
            fullWidth
            className="w-[30rem]"
          />
        </div>
        {/* Phone number */}
        <div className="mb-8">
          <Input
            label="Phone number"
            type="tel"
            name="phone-number"
            placeholder="+82 10-1234-5678"
            value={signupForm.phoneNumber}
            onChange={handleValidatedInputChange('phoneNumber')}
            error={errors.phoneNumber}
            fullWidth
            className="w-[30rem]"
          />
        </div>
        {/* Postal Code */}
        <div className="mb-8">
          <Input
            label="Postal Code"
            type="number"
            name="postal-code"
            placeholder="Enter postal code (optional)"
            value={signupForm.postalCode}
            onChange={handleInputChange('postalCode')}
            fullWidth
            className="w-[30rem]"
          />
        </div>
        {/* Business Address */}
        <div className="mb-8">
          <Input
            label="Business Address"
            type="text"
            name="business-address"
            placeholder="Enter business address"
            value={signupForm.companyAddress}
            onChange={handleValidatedInputChange('companyAddress')}
            error={errors.companyAddress}
            fullWidth
            className="w-[30rem]"
          />
        </div>
        {/* Certificate for Business Registration */}
        <div className="mb-8">
          <FileInput
            label="Certificate for Business Registration"
            name="file"
            accept=".jpg,.jpeg,.png,.gif,.webp,.svg,.pdf,.mp4,.mov,.avi,.wmv,.flv,.webm"
            onChange={handleFileSelect}
            buttonText="Upload"
            isUploading={uploadLoading}
            selectedFileName={selectedFile?.name}
            placeholder="No file selected"
            error={errors.businessCertificate}
            fullWidth={false}
            className="w-[480px]"
          />
        </div>
        {/* Checkboxes */}
        <div className="mb-4 text-left">
          <label className="flex items-center">
            <input
              type="checkbox"
              className="mr-2 inline-block h-5 w-5 cursor-pointer rounded border border-[#bbb] accent-[#8F689A]"
              checked={signupForm.personalInfoAgree}
              onChange={handleCheckboxChange('personalInfoAgree')}
            />
            <Button
              type="button"
              variant="ghost"
              onClick={() => setIsPrivacyModalOpen(true)}
              className="h-auto p-0 text-[14px] font-normal text-[#222]"
            >
              I agree to the Privacy Policy. (Required)
            </Button>
          </label>
          {errors.personalInfoAgree && (
            <p className="ml-2 mt-1 text-xs text-red-500">
              {errors.personalInfoAgree}
            </p>
          )}
        </div>
        <div className="mb-4 text-left">
          <label className="flex items-center">
            <input
              type="checkbox"
              className="mr-2 inline-block h-5 w-5 cursor-pointer rounded border border-[#bbb] accent-[#8F689A]"
              checked={signupForm.termsOfServiceAgree}
              onChange={handleCheckboxChange('termsOfServiceAgree')}
            />
            <Button
              type="button"
              variant="ghost"
              onClick={() => setIsTermsModalOpen(true)}
              className="h-auto p-0 text-[14px] font-normal text-[#222]"
            >
              I agree to Terms of Service. (Required)
            </Button>
          </label>
          {errors.termsOfServiceAgree && (
            <p className="ml-2 mt-1 text-xs text-red-500">
              {errors.termsOfServiceAgree}
            </p>
          )}
        </div>
        <div className="mb-4 flex items-center gap-2">
          <label className="flex items-center">
            <input
              type="checkbox"
              className="mr-2 inline-block h-5 w-5 cursor-pointer rounded border border-[#bbb] accent-[#8F689A]"
              checked={signupForm.marketingAgree}
              onChange={handleCheckboxChange('marketingAgree')}
            />
            <Button
              type="button"
              variant="ghost"
              onClick={() => setIsMarketingModalOpen(true)}
              className="h-auto p-0 text-[14px] font-normal text-[#222]"
            >
              I agree to receive marketing communications. (Optional)
            </Button>
          </label>
        </div>
        <Button
          type="submit"
          variant="primary"
          size="md"
          className="mt-6 w-40 text-[16px] font-bold tracking-wider"
        >
          CREATE
        </Button>
      </form>
      <PrivacyPolicyModal
        isOpen={isPrivacyModalOpen}
        onClose={() => setIsPrivacyModalOpen(false)}
      />
      <TermsOfServiceModal
        isOpen={isTermsModalOpen}
        onClose={() => setIsTermsModalOpen(false)}
      />
      <MarketingInfoPolicyModal
        isOpen={isMarketingModalOpen}
        onClose={() => setIsMarketingModalOpen(false)}
      />
    </section>
  );
};
export default SignUp;
